import React, {useEffect} from 'react';
import {useRoutesElement} from './routes/routes';
import styled, {createGlobalStyle} from 'styled-components';
import {UeThemeProvider} from 'epic-ue-theme';
import {ueClassicDarkTheme} from 'epic-ue-theme/src/classic/ueClassicDarkTheme';
import {makeFormStyleOverrides} from 'epic-ue-theme/src/ueFormStyles';
import {StoreProvider} from './stores';
import {Overrides} from '@material-ui/core/styles/overrides';
import {Footer} from './components/footer/Footer';
import {NavWrapper} from './components/nav/NavWrapper';
import {useNewsletterAccount} from 'epic-ue-newsletter';
import {useAccountStore} from 'epic-ue-shared';
import {useSearchParams} from 'react-router-dom';

const theme = {
    colors: {
        white: '#fff'
    },
    placeholder: {
        color: '#cdcdcd',
        animation: 'react-placeholder-pulse 1.5s infinite'
    },
    fonts: {
        brutal: {
            bold: 'Brutal_Bold',
            medium: 'Brutal_Medium',
            regular: 'Brutal_Regular',
            light: 'Brutal_Light'
        }
    }
};

const GlobalStyle = createGlobalStyle`
    html, body {
        background-color: ${p => p.theme.palette.background.default};
        color: ${p => p.theme.palette.text.primary};
        font-family: OpenSans,sans-serif,arial;
        -webkit-font-smoothing: antialiased;
        &.ko {
            word-wrap: break-word;
            word-break: keep-all;
        }
    }
    .ue-react {
        background-color: ${p => p.theme.palette.background.default};
        color: ${p => p.theme.palette.text.primary};
        font-family: ${p => p.theme.ue.fonts.brutal.regular};
    }

    /**
     Newsletter input box has a double border. MW-1612
     */
    .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-formControl {
        border: none !important;
    }
`;

const AppContainer = styled.div`
    min-height: 50vh;
    background: #1d1d1d;
`;

const AppContent = styled.div`
    min-height: 50vh;
`;

interface Props {
    appState?: any;
}

const App = ({appState = {}}: Props): JSX.Element => {
    //init the account for all newsletter components
    const {account} = useAccountStore();
    const {account: newsletterAccount, setAccount} = useNewsletterAccount();
    useEffect(() => {
        if (account && account.id && (!newsletterAccount || !newsletterAccount.id) && newsletterAccount?.id !== account.id) {
            setAccount(account);
        }
    }, [account, setAccount, newsletterAccount]);

    //MW-1504 remove some query params on client to clean URL for SEO
    const [searchParams, setSearchParams] = useSearchParams();
    const lang = searchParams.get('lang');
    const sessionInvalidated = searchParams.get('sessionInvalidated');
    useEffect(() => {
        let dirty = false;
        if (lang) {
            searchParams.delete('lang');
            dirty = true;
        }
        if (sessionInvalidated) {
            searchParams.delete('sessionInvalidated');
            dirty = true;
        }
        if (dirty) {
            setSearchParams(searchParams, {replace: true});
        }
    }, [lang, sessionInvalidated, setSearchParams]);

    const routes = useRoutesElement();

    ueClassicDarkTheme.overrides = makeFormStyleOverrides(ueClassicDarkTheme) as Overrides;

    const epicGamesFooter = appState.epicGamesFooter || {};
    const epicGamesHeader = appState.epicGamesHeader || {};

    return (
        <UeThemeProvider theme={Object.assign({}, ueClassicDarkTheme, theme)}>
            <GlobalStyle />
            <StoreProvider initialValue={appState}>
                <AppContainer id="deveg-app" className="ue-react">
                    <NavWrapper config={epicGamesHeader} />
                    <AppContent id="deveg-content">{routes}</AppContent>
                    <Footer config={epicGamesFooter} />
                </AppContainer>
            </StoreProvider>
        </UeThemeProvider>
    );
};

export default App;
